import { formatTime, formatOz } from "./dashboard_utils";

const BAR_WIDTH = 6;
const CHART_HEIGHT = 80;
const Y_MAX_OZ = 3.5;
const MIN_LABEL_GAP_PCT = 7;

type Feeding = {
  id: number;
  ended_at: string;
  amount_oz: number | null;
};

const TodayFeedingBarChart = ({
  feedings,
  dayStart,
  now,
}: {
  feedings: Feeding[];
  dayStart: string;
  now: string;
}) => {
  const dayStartMs = new Date(dayStart).getTime();
  const nowMs = new Date(now).getTime();
  const totalMs = Math.max(nowMs - dayStartMs, 1);

  const barsWithAmount = feedings
    .filter((f) => f.amount_oz != null)
    .slice()
    .sort(
      (a, b) => new Date(a.ended_at).getTime() - new Date(b.ended_at).getTime(),
    );

  // Only labels for feedings spaced far enough apart (in % of width) get shown,
  // so vertical time labels never overlap regardless of how bunched the bars are.
  function getXAxisLabels() {
    const labels: { label: string; pct: number; align: string }[] = [
      { label: "12am", pct: 0, align: "left" },
    ];
    let lastPct = 0;
    barsWithAmount.forEach((f) => {
      const pct = ((new Date(f.ended_at).getTime() - dayStartMs) / totalMs) * 100;
      if (pct - lastPct >= MIN_LABEL_GAP_PCT && 100 - pct >= MIN_LABEL_GAP_PCT) {
        labels.push({ label: formatTime(f.ended_at), pct, align: "center" });
        lastPct = pct;
      }
    });
    labels.push({ label: "now", pct: 100, align: "right" });
    return labels;
  }

  return (
    <div style={{ padding: "0 4px", marginTop: 16 }}>
      <div
        style={{
          position: "relative",
          height: CHART_HEIGHT,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          borderLeft: "1px solid rgba(0,0,0,0.12)",
          overflow: "visible",
        }}
      >
        {barsWithAmount.length === 0 ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span className="text-muted" style={{ fontSize: "0.75rem" }}>
              no amounts recorded
            </span>
          </div>
        ) : (
          barsWithAmount.map((feeding) => {
            const t = new Date(feeding.ended_at).getTime();
            const xPct = ((t - dayStartMs) / totalMs) * 100;
            const hPct = Math.min((feeding.amount_oz! / Y_MAX_OZ) * 100, 100);
            const barLeft = `calc(${xPct}% - ${BAR_WIDTH / 2}px)`;

            return (
              <div
                key={feeding.id}
                title={`${feeding.amount_oz} oz at ${formatTime(feeding.ended_at)}`}
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: barLeft,
                  width: BAR_WIDTH,
                  height: `${hPct}%`,
                  minHeight: 4,
                  background: "var(--bs-primary)",
                  borderRadius: "2px 2px 0 0",
                  opacity: 0.8,
                }}
              />
            );
          })
        )}
      </div>

      {/* X-axis labels */}
      <div style={{ position: "relative", height: 40, marginTop: 4 }}>
        {getXAxisLabels().map(({ label, pct, align }) => {
          let transform;
          let left;
          if (align === "left") {
            left = `0%`;
            transform = "rotate(-90deg) translateY(-100%)";
          } else if (align === "right") {
            left = `100%`;
            transform = "rotate(-90deg) translateY(-100%)";
          } else {
            left = `${pct}%`;
            transform = "translateX(-50%) rotate(-90deg) translateY(-100%)";
          }
          return (
            <span
              key={label + pct}
              style={{
                position: "absolute",
                left,
                top: "90%",
                fontSize: "0.7rem",
                color: "var(--bs-secondary)",
                transform,
                transformOrigin: "top",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

type TodayFeedingsSummary = {
  day_start: string;
  now: string;
  window_total_oz: number;
  window_count: number;
  has_amounts: boolean;
  feedings: Feeding[];
};

type Props = {
  todayFeedings: TodayFeedingsSummary;
};

export const TodayFeedingsWidget = ({ todayFeedings }: Props) => {
  const { day_start, now, window_total_oz, feedings } = todayFeedings;

  return (
    <div className="col-12 col-md-6">
      <div className="card border-0 shadow bg-gray-3 my-4 dashboard-card dashboard-card-today-feeding">
        <div className="card-body text-center">
          <div className="fs-24 fw-bold text-muted text-uppercase">
            Feedings Today
          </div>

          <div className="fs-48 desktop-fs-90 fw-bold mt-2">
            {formatOz(window_total_oz)} oz
          </div>

          <TodayFeedingBarChart
            feedings={feedings}
            dayStart={day_start}
            now={now}
          />
        </div>
      </div>
    </div>
  );
};
